import { applyFilterMutation } from 'gqlQueries/filterQueries';

export const PPA_SCOPE_DIMENSIONS = ['dim1', 'dim2', 'dim3', 'dim4'] as const;
export const PPA_SCOPE_FEATURE_NAME = 'default';

type ScopeInclusion = {
    dimension?: string;
    attributeType?: string;
    attributeDesc?: string;
    attributeExId?: string;
    isActive?: boolean;
    isAttribute?: boolean;
};

export function parseScopeDimToSelection(scopeValue: unknown): string | undefined {
    try {
        if (!scopeValue) return undefined;
        const parsedScope = typeof scopeValue === 'string' ? JSON.parse(scopeValue) : scopeValue;
        const inclusion = parsedScope?.inclusion?.[0];
        return inclusion?.attributeDesc || inclusion?.attributeExId;
    } catch {
        return undefined;
    }
}

/**
 * Reads saved scope (dim1/dim2 JSON payloads) and stages them into
 * allCheckedAttribute / applyFilterObject so the Global Filter sidebar
 * reflects the inherited scope without triggering a filter apply.
 *
 * Returns null when no matching attribute types are found (e.g. scope
 * attribute types are not yet loaded).
 */
export function buildGlobalFilterUiStateFromPersistedScope(
    persistedScope: Record<string, unknown>,
    attributeTypes: Record<string, string[]>,
) {
    const allCheckedAttribute: Record<string, Record<string, ScopeInclusion>> = {};
    const applyFilterObject: Record<string, Record<string, { inclusion?: ScopeInclusion[] }>> = {};
    const attributeTypeLookup = new Map<string, string>();

    for (const dimension of Object.keys(attributeTypes)) {
        applyFilterObject[dimension] = {};
        attributeTypes[dimension].forEach((attributeType) => {
            applyFilterObject[dimension][attributeType] = {};
            allCheckedAttribute[attributeType] = {};
            attributeTypeLookup.set(attributeType.toLowerCase(), attributeType);
        });
    }

    PPA_SCOPE_DIMENSIONS.forEach((dimension) => {
        const scopeValue = persistedScope[dimension];
        if (!scopeValue) return;

        try {
            const parsedScope = typeof scopeValue === 'string' ? JSON.parse(scopeValue) : scopeValue;
            const inclusions: ScopeInclusion[] = parsedScope?.inclusion ?? [];

            inclusions.forEach((inclusion) => {
                const resolvedType = attributeTypeLookup.get(inclusion?.attributeType?.toLowerCase() ?? '');
                const attributeKey = inclusion?.attributeExId || inclusion?.attributeDesc;
                if (!resolvedType || !attributeKey) return;

                allCheckedAttribute[resolvedType][attributeKey] = inclusion;
                if (applyFilterObject[dimension]?.[resolvedType] !== undefined) {
                    applyFilterObject[dimension][resolvedType] = { inclusion: [inclusion] };
                }
            });
        } catch {
            // Ignore malformed payloads.
        }
    });

    const hasSelections = Object.values(allCheckedAttribute).some(
        (entries) => Object.keys(entries).length > 0,
    );

    return hasSelections ? { allCheckedAttribute, applyFilterObject } : null;
}

export function mergeApplyFilterObjects(
    base: Record<string, Record<string, unknown>>,
    staged: Record<string, Record<string, { inclusion?: ScopeInclusion[] }>>,
) {
    const merged = { ...base };
    Object.keys(staged).forEach((dimension) => {
        merged[dimension] = { ...(merged[dimension] ?? {}) };
        Object.entries(staged[dimension]).forEach(([attributeType, payload]) => {
            if (payload?.inclusion?.length) {
                merged[dimension][attributeType] = payload;
            }
        });
    });
    return merged;
}

export function buildScopeDimensionPayloads(persistedScope: Record<string, any>) {
    return PPA_SCOPE_DIMENSIONS.reduce<Record<string, string | null>>((acc, dimension) => {
        acc[`jsonstring_${dimension}`] = persistedScope?.[dimension] || null;
        return acc;
    }, {});
}

export function hasPpaScopeFilterIds(scopeFilterIds?: {
    dim1?: { ids?: unknown[] };
    dim2?: { ids?: unknown[] };
}) {
    return (
        (scopeFilterIds?.dim1?.ids?.length ?? 0) > 0 ||
        (scopeFilterIds?.dim2?.ids?.length ?? 0) > 0
    );
}

export function hasPersistedUserScope(persistedScope?: Record<string, any> | null) {
    return Boolean(persistedScope?.dim1 || persistedScope?.dim2);
}

export { applyFilterMutation };
